import { AsyncPipe } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { IOptionItem, SelectComponent } from '@ui-lib/components';
import { map, scan, shareReplay } from 'rxjs';
import { IDictionaryItem } from '../../types/dictionary-item.interface';
import { NoopValueAccessorDirective } from '../facility-select/noop-value-accessor.directive';
import { WorkerStatusDictionaryService } from '../worker-status-selector/worker-status-dictionary.service';

@Component({
  selector: 'mon-worker-status-select',
  templateUrl: './worker-status-select.component.html',
  styleUrl: './worker-status-select.component.scss',
  imports: [AsyncPipe, ReactiveFormsModule, SelectComponent],
  hostDirectives: [NoopValueAccessorDirective],
})
export class WorkerStatusSelectComponent {
  private readonly ngControl = inject(NgControl, { self: true, optional: true });

  protected readonly workerStatusDictionaryService = inject(WorkerStatusDictionaryService);

  @Input()
  public labelTranslateKey = 'monitoring.worker-status';

  @Input()
  public fullWidth = false;

  @Input()
  public isClearable = false;

  protected readonly workerStatusOptions$ = this.workerStatusDictionaryService.source$.pipe(
    scan(
      (
        acc: IDictionaryItem<number>[],
        newbies: IDictionaryItem<number>[],
      ): IDictionaryItem<number>[] => [...acc, ...newbies],
      [],
    ),
    map((items: IDictionaryItem<number>[]): IDictionaryItem<number>[] => {
      const seenIds = new Set<number>();

      return items.filter((item: IDictionaryItem<number>): boolean => {
        if (item.id === undefined) return false;

        if (seenIds.has(item.id)) {
          return false;
        }

        seenIds.add(item.id);

        return true;
      });
    }),
    map((items): IOptionItem<number>[] =>
      items.map(
        (item): IOptionItem<number> => ({
          id: item.id,
          label: item.name,
        }),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public get control(): FormControl<IOptionItem<number> | null> {
    const control = this.ngControl?.control;

    if (!control) {
      throw new Error('mon-worker-status-select must be used with formControl or formControlName');
    }

    if (!(control instanceof FormControl)) {
      throw new Error('mon-worker-status-select supports only FormControl');
    }

    return control as FormControl<IOptionItem<number> | null>;
  }
}
